import React,{ useState, useEffect } from "react";
import { connect } from "react-redux";
import dayjs from "dayjs";
import Card from "components/card";
import Button  from "components/button";
import ButtonWithConfirm from "components/button/buttonwithconfirm";
import PopupButton from "components/button/PopupButton";
import CandidateScoreCard from "./CandidateScoreCard";
import AddNotesForm from "./AddNotesForm";
import { getNameInitials, formatDateTimeOrNull } from "utils/index";
import {
    Mail,
    Phone,
    MapPin,
    CheckCircle2,
    CircleX,
    CircleArrowLeft,
    Sparkles,
    GraduationCap,
    CirclePlus,
    Folder,
} from "lucide-react";
import { 
	doAdvanceApplicant, doDeclineApplicant, doMoveBackApplicant,
    getApplicantNotes
} from "../../../../redux/recruitment/recruitmentState";
import DownloadButton from "components/button/download";
import ResumePreview from "./ResumePreview";
import JobRoleOfferForm from "./JobRoleOfferForm";
import {
  getApplicantJobRoleOffer 
} from "../../../../redux/jobroleoffer/jobRoleOfferState";

const CandidateProfile = ({ 
    authUser, 
    candidate, 
    updateApplicantStatus, 
    declineApplicant, 
    moveBackApplicant,
    getCandidateNotes,
    candidateNotes,
    getCandidateOffer,
    candidateOffer,
 }) => {

    const [isOpen, setIsOpen] = useState(false);
    const isClient = authUser?.user_level === 4;

    const handleAdvance = () => {
        updateApplicantStatus({id:candidate.jap_id, pipelineId:candidate.jap_pipeline_id});
        setIsOpen(false);  
    };

    const handleNotSuitable = () => {
        declineApplicant({id:candidate.jap_id});
        setIsOpen(false);  
    };

    const handleMoveBack = () => {
        moveBackApplicant({id:candidate.jap_id, pipelineId:candidate.jap_pipeline_id});
        setIsOpen(false);  
    };

    useEffect(() => {
        if(candidate){
            getCandidateNotes({id:candidate.jap_id});
        }
    }, [getCandidateNotes, candidate]);

    useEffect(() => {
        if(candidate.jap_pipeline_id >= 44){
            getCandidateOffer({ id: candidate.jap_id});
        }
    }, [candidate, getCandidateOffer]);

    if (!candidate) return null;

    return (
        <Card className="flex flex-col gap-6 rounded-2xl border-2 border-emerald-500 shadow-xl bg-white lg:sticky lg:top-6 overflow-visible">
            <div className="grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 [.border-b]:pb-6 bg-gradient-to-br from-emerald-50 to-cyan-50 border-b border-slate-200 rounded-t-2xl p-4 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3">
                    <span className="relative flex w-14 h-14 shrink-0 overflow-hidden rounded-full border-2 border-white shadow-md bg-gradient-to-br from-emerald-500 to-cyan-500">
                        <span className="flex size-full items-center justify-center font-semibold text-[16px] sm:text-[18px] text-white">
                            {getNameInitials(candidate?.applicant_name)}
                        </span>
                    </span>
                    <div className="min-w-0 flex-1">
                        <h4 className="text-[16px] sm:text-[18px] truncate">{candidate?.applicant_name}</h4>
                        <p className="text-slate-700 text-[12px] sm:text-[13px] truncate">{candidate?.job_post_title}</p>
                    </div>
                </div>
            </div>

            <div className="pb-6 p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-5 overflow-x-hidden">
                {!isClient && 
                    <>
                    <div>
                        <h4 className="font-semibold text-[13px] flex items-center gap-2 mb-2 text-gray-800">
                            <Mail className="w-4 h-4 text-emerald-600" /> Contact Information
                        </h4>
                        <div className="space-y-2 text-[12px] text-slate-700">
                            <div className="flex items-center gap-2">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                <a href={`mailto:${candidate?.applicant_email}`} className="text-emerald-600 truncate hover:underline">
                                    {candidate?.applicant_email}
                                </a>
                            </div>
                            <div className="flex items-center gap-2">
                                <Phone className="w-3 h-3 flex-shrink-0" />
                                <span className="text-slate-900">{candidate?.applicant_phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="text-slate-900">{candidate?.applicant_location}</span>
                            </div>
                        </div>
                    </div>

                    <hr className="border-t border-gray-200" />
                    </>
                }

                <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-[13px] flex items-center gap-2 mb-2 text-gray-800">
                        <GraduationCap className="w-4 h-4 text-emerald-600" /> Background
                    </h4>
                    <div className="text-[12px] space-y-1.5">
                        <div>
                            <p className="text-slate-700 text-[11px]">Experience</p>
                            <p className="font-medium">{candidate?.applicant?.apl_xp_years} years</p>
                        </div>
                        <div>
                            <p className="text-slate-700 text-[11px]">Education</p>
                            <p className="font-medium">{candidate?.applicant?.apl_highest_degree}</p>
                        </div>
                        {!isClient &&
                        <div>
                            <p className="text-slate-700 text-[11px]">Applied Date</p>
                            <p className="font-medium">{formatDateTimeOrNull(candidate?.created_at, "YYYY-MM-DD")}</p>
                        </div>
                        }
                    </div>
                </div>

                <hr className="border-t border-gray-200" />

                <div className="space-y-2 sm:space-y-3">
                    <h4 className="font-semibold text-[13px] mb-2 flex items-center gap-2 text-gray-800">
                        <Sparkles className="w-4 h-4 text-emerald-600" />
                        <span>Skills</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {candidate?.applicant?.skills?.map((skill) => (
                            <span
                                key={skill}
                                className="inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-medium whitespace-nowrap hover:bg-emerald-200 transition"
                            >
                                {skill}
                            </span>
                        ))}
                    </div>
                </div>

                {isClient && 
                    <CandidateScoreCard scoreData={candidate?.applicant_score} isClientUser={true}/>
                }
                
                <hr className="border-t border-gray-200" />

                { candidateNotes?.length > 0 && 
                    <div className="space-y-2 sm:space-y-3">
                        <h4 className="font-semibold text-[13px] mb-2">Notes</h4>
                        {candidateNotes.map((note) => (
                            <div key={note.apn_id} className="text-[12px] bg-slate-50 border border-slate-200 rounded-lg p-3 text-gray-700 leading-relaxed">
                                <div className="text-black font-medium">{note.apn_notes}</div>
                                <div className="flex flex-row justify-between mt-2">
                                    <div className="text-[9px] text-slate-500">By {note.apn_note_by}</div>
                                    <div className="text-[9px] text-slate-500">{dayjs(note.created_at).format("YYYY-MM-DD")}</div>
                                </div>
                            </div>
                            
                        ))}
                    </div>
                }

                <div className="space-y-2">
                    <PopupButton 
                        className="access-button btn-next-stage w-full"
                        label="Add Notes"
                        useCustomClass={true}
                        icon={CirclePlus}
                        title="Add Notes"
                        modalWidth={600}
                        id="add-notes-modal"
                        >
                        <AddNotesForm candidate={candidate} />
                    </PopupButton>

                    {isClient ?
                        <PopupButton 
                            className="access-button btn-next-stage w-full"
                            label="View Resume"
                            useCustomClass={true}
                            id="view-resume-modal"
                            title={candidate?.applicant_name + "'s Resume"}
                            >
                            <ResumePreview applicant={candidate?.applicant} isClientUser={true}/>
                        </PopupButton>
                        :
                        <>
                            <PopupButton 
                                className="access-button btn-next-stage w-full"
                                label="Preview Resume"
                                useCustomClass={true}
                                id="view-resume-modal"
                                title={candidate?.applicant_name + "'s Resume"}
                                >
                                <ResumePreview applicant={candidate?.applicant} isClientUser={false}/>
                            </PopupButton>

                            <DownloadButton 
                                className="w-full py-2 text-[13px]"
                                type="recruitment"
                                downloadPath={`download/applicant/${candidate?.applicant?.apl_id}/resume`} 
                                title="Download Resume"/>

                            {candidate.jap_pipeline_id >= 44 && <>
                                <PopupButton 
                                    className="access-button btn-next-stage w-full"
                                    label={`${candidateOffer?.jof_id > 0 ? 'Job Offer Details' : 'Create Job Offer'}`}
                                    useCustomClass={true}
                                    id="job-role-offer-modal"
                                    title={`${candidateOffer?.jof_id > 0 ? 'Job Offer Details' : 'Create Job Offer'} - ${candidate?.applicant_name}`}
                                    modalWidth={500}
                                    icon={Folder}
                                    >
                                    <JobRoleOfferForm candidate={candidate}/>
                                </PopupButton>
                            </>
                            }
                        </>
                        
                    }

                    {(!isClient || (isClient && candidate.jap_pipeline_id <= 42))&& 
                    <div className="flex w-full flex-wrap gap-2">
                        <ButtonWithConfirm
                            className="access-button btn-decline flex-grow"
                            confirmLabel="Decline"
                            confirmationTitle="Not Suitable"
                            confirmationMessage={<>Are you sure you want to decline <span className="font-medium">{candidate?.applicant_name}</span>?</>}
                            confirmHandler={handleNotSuitable}
                        >
                            <CircleX className="w-3 h-3" />
                            <span>Not Suitable</span>
                        </ButtonWithConfirm>

                        <ButtonWithConfirm
                            className="access-button btn-decline flex-grow"
                            confirmLabel="Move Back"
                            confirmationTitle="Move Back"
                            confirmationMessage={<>Are you sure you want to move back <span className="font-medium">{candidate?.applicant_name}</span> to previous stage in the pipeline?</>}
                            confirmHandler={handleMoveBack}
                        >
                            <CircleArrowLeft className="w-3 h-3" />
                            <span>Move Back</span>
                        </ButtonWithConfirm>

                        <ButtonWithConfirm
                            className="access-button btn-next-stage flex-grow"
                            confirmLabel="Advance"
                            confirmationTitle="Advance to Next Stage"
                            confirmationMessage={<>Are you sure you want to advance <span className="font-medium">{candidate?.applicant_name}</span> to the next stage in the pipeline?</>}
                            confirmHandler={handleAdvance}
                        >
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Advance to Next Stage</span>
                        </ButtonWithConfirm>

                    </div>
                    }
                    {!isClient && <>
                        
                        

                        <Button className="w-full h-9 flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2 text-[13px] text-gray-800 font-medium hover:bg-gray-50 transition">
                            <Mail className="w-3 h-3" /> 
                            <span>Contact Candidate</span>
                        </Button>
                    </>
                    }
                </div>
            </div>
        </Card>
    );
};

const mapStateToProps = (state) => ({
    authUser: state.auth.user,
    candidateNotes: state.recruitment.applicantNotes?.items,
    candidateOffer: state.jobRoleOffer.jobRoleOfferDetails?.data,
});

const mapDispatchToProps = (dispatch) => ({
    updateApplicantStatus: (data) => dispatch(doAdvanceApplicant(data)),
    declineApplicant: (data) => dispatch(doDeclineApplicant(data)),
    moveBackApplicant: (data) => dispatch(doMoveBackApplicant(data)),
    getCandidateNotes: (data) => dispatch(getApplicantNotes(data)),
    getCandidateOffer: (data) => dispatch(getApplicantJobRoleOffer(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CandidateProfile);
